import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import Clarity from '@microsoft/clarity';
import { Authentication, BootstrapService } from '@sb/library/api';
import { Field, IFrameRefreshMessage, Scope } from '@sb/library/base';
import { WebsiteBlockDisplayItem, WebsiteBlockType, WebsiteDisplayItem, WebsiteRenderContextDisplayItem } from '@sb/library/data-models';
import { LocalizationService } from '@sb/library/localization';
import { StyleManager, WebsiteManager } from '@sb/library/managers';
import { CreateDefaultPageNotFoundPage } from '@shared/definitions/website.definitions';
import { NEVER, Observable, of } from 'rxjs';

export class WebsitePortalScope extends Scope {
    websiteBlock = Field<WebsiteBlockDisplayItem | undefined>();
    website = Field<WebsiteDisplayItem>();
    idByPath = Field<Record<string, string>>();
    pathAndTitleByID = Field<Record<string, [string, string]>>();
    renderContext = Field<WebsiteRenderContextDisplayItem>();
}

@Injectable({
    providedIn: 'root'
})
export class AppBootstrapService extends BootstrapService implements WebsitePortalScope {
    readonly Scope = this.SetScope({
        Implements: WebsitePortalScope
    });

    readonly storageKeys = null;

    private _renderContext!: WebsiteRenderContextDisplayItem;
    private _website!: WebsiteDisplayItem;
    private _websiteBlock?: WebsiteBlockDisplayItem;

    private _idByPath: Record<string, string> = {};
    private _pathAndTitleByID: Record<string, [string, string]> = {};

    constructor(
        private _LocalizationService: LocalizationService,
        private _WebsiteManager: WebsiteManager,
        private _StyleManager: StyleManager
    ) {
        super();
    }

    Bootstrap(): Observable<any> {
        const websiteData = document.getElementById('sb-bootstrap-website')?.getAttribute('data-json');
        if (!websiteData) {
            this.HandleError();
            return NEVER; // Never resolve the APP_INITIALIZER so the app is never bootstrapped.
        }

        this._website = JSON.parse(websiteData);
        const websiteBlockData = document.getElementById('sb-bootstrap-websiteblock')?.getAttribute('data-json');
        if (websiteBlockData) {
            this._websiteBlock = JSON.parse(websiteBlockData);
            this.SetRenderContext(this._websiteBlock!.RenderContext!);

            Clarity.init(environment.Telemetry!.Clarity!.ProjectID!);
            Clarity.identify("Anonymous");

            Clarity.setTag("Portal", "Widget");
            Clarity.setTag("School.ID", this._website!.SchoolID!);
            Clarity.setTag("School.IsMultiTeacher", String(this._websiteBlock!.RenderContext!.SchoolIsMultiTeacher));
        } else {
            this.SetUpPagePaths();
            this.SetRenderContext(this._website!.RenderContext!);
        }

        return of(null);
    }

    private SetUpPagePaths() {
        this._website.WebsitePages!.forEach(page => {
            this._idByPath[page.Slug!] = page.ID!
            this._pathAndTitleByID[page.ID!] = [page.Slug!, page.SEOTitle ? page.SEOTitle : `${this._website.Title} - ${page.Title}`];
            page.ChildPages?.forEach(childPage => {
                this._idByPath[childPage.Slug!] = childPage.ID!
                this._pathAndTitleByID[childPage.ID!] = [childPage.Slug!, childPage.SEOTitle ? childPage.SEOTitle : `${this._website.Title} - ${childPage.Title}`];
            })
        });

        const homepage = this.website.WebsitePages.find(p => p.IsHomepage)!;
        this._website.WebsitePages.push(CreateDefaultPageNotFoundPage(homepage.Title!));
        this._idByPath['/'] = homepage.ID!;
        this._idByPath['/index.htm'] = homepage.ID!;
        this._idByPath['/index.html'] = homepage.ID!;
    }

    private SetRenderContext(renderContext: WebsiteRenderContextDisplayItem) {
        this._renderContext = renderContext;
        this._LocalizationService.Initialize(renderContext.LocalizationInformation!);

        const websiteBlock = this._websiteBlock;
        switch (websiteBlock?.WebsiteBlockType) {
            case WebsiteBlockType.SignupForm:
                Authentication.SetFromJWT(renderContext.SignupFormRenderContext!.Jwt!);
                break;
            case WebsiteBlockType.LoginForm:
                Authentication.SetFromJWT(renderContext.LoginFormRenderContext!.Jwt!);
                break;
            case WebsiteBlockType.ContactForm:
                Authentication.SetFromJWT(renderContext.ContactFormRenderContext!.Jwt!);
                break;
        }

        const website = this._website!;
        const accentColor = website.AccentColor ?? this._StyleManager.GetCSSPropertyValue('--primary-brand-500');
        const backgroundColor = website.BackgroundColor ?? this._StyleManager.GetCSSPropertyValue('--base-0');

        // Below not setting accent text color / removing text color picker from UI bc user could select white text WB-296
        // const accentTextColor = this._StyleManager.GetCSSPropertyValue('--base-300');

        const colors = [
            { cssVariable: '--brand-0', alpha: 146 },
            { cssVariable: '--brand-50', alpha: 131 },
            { cssVariable: '--brand-100', alpha: 117 },
            { cssVariable: '--brand-200', alpha: 88 },
            { cssVariable: '--brand-300', alpha: 54 },
            { cssVariable: '--brand-400', alpha: 29 },
            { cssVariable: '--brand-500', alpha: 0 },
            { cssVariable: '--brand-600', alpha: -15 },
            { cssVariable: '--brand-700', alpha: -33 },
            { cssVariable: '--brand-800', alpha: -49 },
            { cssVariable: '--brand-900', alpha: -66 },
        ];

        colors.forEach(color => {
            this.SetColorVariables(color.cssVariable, accentColor, color.alpha);
        });

        this._StyleManager.ApplyBrandColorScale(accentColor, undefined);

        this.SetWidgetAccentContrastColor(accentColor);
        this._WebsiteManager.SetBlockAccentBackgroundColor(accentColor);

        if (!websiteBlock) {
            this._WebsiteManager.SetWebsiteColors(backgroundColor!, accentColor!, website.HeaderColor!, website.FooterColor!);
        }

        this._WebsiteManager.SetBlockTextFont(website.HeadingFontFamily!, website.BodyFontFamily!);
    }

    private SetWidgetAccentContrastColor(accent: string): void {
        this._WebsiteManager.SetWidgetAccentContrastColor(accent);
    }

    private AddTransparency(color: string, alpha: number): string {
        return '#' + color.replace(/^#/, '').replace(/../g, color => ('0'+Math.min(255, Math.max(0, parseInt(color, 16) + alpha)).toString(16)).substr(-2));
    }

    private SetColorVariables(cssVariable: string, color: string, alpha: number): void {
        document.documentElement.style.setProperty(cssVariable, this.AddTransparency(color, alpha));
    }

    private HandleError() {
        let retries = 40;

        setTimeout(function reloadParent() {
            if ((window as any).parentIFrame) {
                (window as any).parentIFrame.sendMessage(new IFrameRefreshMessage());
            } else if (retries-- > 0) {
                setTimeout(reloadParent, 200);
            }
        }, 200);

        // If we cannot reload the outer page (e.g., external widget) show an error
        setTimeout(() => {
            const loadElement = document.getElementsByTagName("app-root")[0];
            const failElement = document.getElementById("widget-load-failed")!;
            loadElement.classList.add('d-none');
            failElement.classList.remove('d-none');
            failElement.classList.add('d-flex');

            const errorData = document.getElementById('sb-error')?.getAttribute('data-json');
            if (errorData) {
                const errorElement = document.getElementById("error-info")!;
                errorElement.innerHTML = '<p class="mb-0" style="font-family: monospace;">' + JSON.parse(errorData) + '</p>';
            }
        }, 8000);
    }

    get websiteBlock(): WebsiteBlockDisplayItem | undefined {
        return this._websiteBlock;
    }

    get website(): WebsiteDisplayItem {
        return this._website!;
    }

    get idByPath(): Record<string, string> {
        return this._idByPath;
    }

    get pathAndTitleByID(): Record<string, [string, string]> {
        return this._pathAndTitleByID;
    }

    get renderContext(): WebsiteRenderContextDisplayItem {
        return this._renderContext!;
    }
}
